// eslint-disable-next-line no-restricted-imports
import { usePathname as useNextPathname, useRouter as useNextRouter, useSearchParams as useNextSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { appRoutes } from '@/app/_routes';
import { isClient } from '@/app/_utils/env';
import { ParseUrlOptions, parseUrl } from '@/app/_utils/url';

const navigationHistory: string[] = [];

/**
 * Hook to navigate to a different page
 * @param toggleIsNavigating: Whether to toggle the isNavigating state.
 * @returns A function to navigate to a different page and the isNavigating state.
 * @param appRouteKey: The key of the app route to navigate to. If null, the current path will be used.
 * @param options.queryParams: The query parameters to add to the URL.
 * @param options.keepPreviousQueryParams: Whether to keep the previous query parameters.
 * @param options.replaceRoute: Whether to replace the current route or push a new one.
 */
type UseNavigate = {
  toggleIsNavigating?: boolean;
};

type UseNavigateResult = {
  navigate: (appRouteKey: string | null, options?: NavigateOptions) => void;
  isNavigating: boolean;
};

export type NavigateRouteKey = string | null;

export type NavigateOptions = {
  queryParams?: ParseUrlOptions['queryParams'];
  urlParams?: ParseUrlOptions['urlParams'];
  keepPreviousQueryParams?: boolean;
  replaceRoute?: boolean;
};

export const useNavigate = ({ toggleIsNavigating = false }: UseNavigate = {}): UseNavigateResult => {
  const router = useNextRouter();
  const pathname = useNextPathname();
  const searchParams = useNextSearchParams();
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    if (toggleIsNavigating && isNavigating) {
      setIsNavigating(false);
    }
  }, [pathname, searchParams.toString()]);

  const navigate = (appRouteKey: NavigateRouteKey, options: NavigateOptions): void => {
    if (!isClient()) {
      return;
    }

    // Get path
    let path = '';
    if (appRouteKey === null) {
      path = window.location.pathname;
    } else if (appRouteKey in appRoutes) {
      path = appRoutes[appRouteKey].path;
    } else {
      throw new Error(`useNavigate error: route "${appRouteKey}" not found in appRoutes`);
    }

    // Get query params
    const queryParams = {
      ...(options?.keepPreviousQueryParams ? Object.fromEntries(searchParams) : {}),
      ...(options?.queryParams || {})
    };

    // Get href
    const href = parseUrl(path, { urlParams: options?.urlParams || {}, queryParams });

    // Navigate
    if (href === `${window.location.pathname}${window.location.search}`) {
      window.location.reload();
    } else if (options?.replaceRoute) {
      router.replace(href);
    } else {
      router.push(href);
    }

    // Toggle navigating state
    if (toggleIsNavigating) {
      setIsNavigating(true);
    }

    // Add to navigation history
    navigationHistory.push(href);
  };

  return { navigate, isNavigating };
};

/**
 * Hook to get a query parameter from the URL
 * @param paramKey: The key of the query parameter to get
 * @param defaultValue: The default value to return if the query parameter is not found
 * @param type: The type of the query parameter to get
 * @returns The value of the query parameter
 */
export const useQueryParam = <T extends string | number | boolean | null | undefined>(
  paramKey: string,
  defaultValue: T = '' as T,
  type: 'string' | 'number' | 'boolean' = 'string'
): T => {
  const searchParams = useNextSearchParams();
  const value = searchParams.get(paramKey) || '';
  if (type === 'number') {
    return (isNaN(parseInt(value)) ? defaultValue : parseInt(value)) as T;
  } else if (type === 'boolean') {
    return (value === 'true') as T;
  }
  return (value || defaultValue) as T;
};

/**
 * Hook to get the current pathname
 * @returns The current pathname
 */
export const usePathname = (): string => {
  return useNextPathname();
};

/**
 * Hook to get the current URL
 * @returns The current URL
 */
export const useUrl = () => {
  const pathname = useNextPathname();
  const searchParams = useNextSearchParams();
  return `${pathname}?${searchParams.toString()}`;
};

/**
 * Hook to navigate back
 * @returns A function to navigate back
 */
export const useNavigateBack = () => {
  return useNextRouter().back;
};

/**
 * Navigation history
 * @returns The last navigation path
 */
export const getLastNavigationPath = () => {
  return navigationHistory[navigationHistory.length - 1] || '';
};
